import constants from './indesignEditor/constants';
import { LinkType, editedElementClassName } from '../constants';
import UrlUtils from '../../../utils/url';
import { Api } from '../../../typings/api';
import Utils from '../../../utils';
import store from '../../../store';
import UserAgentUtils from '../../../utils/userAgent';

interface ElementSize {
	width: number;
	height: number;
}

interface StyleProps {
	transform: string | null;
}

interface OptimizedImageData {
	width: number;
	height: number;
	src: string;
}

export enum TooltipAction {
	SHOW = 'showTooltip',
	HIDE = 'hideTooltip',
}

export const getPageHtmlPath = (path: string, page: number) => path.replace('%{pageNumber}', page.toString());

export const getNumberFromString = (str: string): number => {
	const regExp = /[+-]?\d+(\.\d+)?/g;
	const match = str.match(regExp);
	return match ? +match[0] : 0;
};

export const getRoundedValue = (value: number, decimal: number = 100): number => (
	Math.round(value * decimal) / decimal
);

export const getValueAndUnits = (value: string): { value: number, units: string } => {
	const valueWithoutUnits = getNumberFromString(value);
	const units = value.replace(valueWithoutUnits.toString(), '');

	return {
		value: valueWithoutUnits,
		units,
	};
};

export const youtubeUrl = 'https://www.youtube.com/embed/';
const youtubePreviewUrl = 'https://i.ytimg.com/vi/';
const youtubeUrlRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;

const getYoutubeVideoId = (url: string): string => {
	const match = url.match(youtubeUrlRegex);

	if (!match) {
		return '';
	}

	return match[2].split('" ')[0];
};

export const isYoutubeLink = (url: string): boolean => url.includes(youtubeUrl);

export const getYoutubeLink = (url: string): string => {
	const videoId = getYoutubeVideoId(url);
	return videoId ? `${youtubeUrl}${videoId}` : '';
};

export const getYoutubeVideoCover = (url: string): string => {
	const videoId = getYoutubeVideoId(url);
	return `${youtubePreviewUrl}${videoId}/0.jpg`;
};

export const getVideoCover = (url: string, isDefaultVideoInHtmlEditor?: boolean): string => {
	if (isDefaultVideoInHtmlEditor) {
		return `${url}-cover.jpg`;
	}

	return url.replace(/\.[^.]+$/, '/cover.jpg');
};

const mediaParamRegex = /\$media\$|\${media}|\$%7Bmedia%7D/;
export const hasMediaParam = (str: string): boolean => !!str.match(mediaParamRegex);

export const getRelUrlFromStringWithMediaParam = (str: string): string => {
	const getMediaParam = (str: string): string => {
		const match = str.match(mediaParamRegex);
		return match ? match[0] : '';
	};
	return str.replace(getMediaParam(str), '');
};

export const getSelectorStylesFromCss = (selector: string, css: string): string => {
	const regExp = new RegExp(`${selector}.*?(\\s*\\{[^}]*\\})`);
	const extraSymbolsInStylesRegExp = /{|}|\s+(?=(?:[^\'"]*[\'"][^\'"]*[\'"])*[^\'"]*$)/g;
	const match = css.match(regExp);
	return match ? match[1].replace(extraSymbolsInStylesRegExp, '') : '';
};

export const getElementSizes = (id: string, css: string): ElementSize => {
	const styles = getSelectorStylesFromCss(`#${id}`, css);
	return styles
		.split(';')
		.filter((style) => {
			const styleName = style.split(':')[0];
			return styleName === 'width' || styleName === 'height';
		})
		.reduce((acc, current) => {
			const [key, value] = current.split(':');
			acc[key] = getNumberFromString(value);
			return acc;
		}, {} as ElementSize);
};

export const getElementScale = (element: HTMLElement, css: string): number => {
	const {
		id,
		dataset: {
			[constants.animationEndStateDataAttribute]: animationClassName,
		},
	} = element;
	const styles = getSelectorStylesFromCss(id, css);
	const regExp = /scale\((.+?)\)/;

	let scale = 1;

	const stylesMatch = styles.match(regExp);
	if (stylesMatch) {
		scale = +stylesMatch[1].split(',')[0];
	}

	if (animationClassName) {
		const animationStyles = getSelectorStylesFromCss(animationClassName, css);
		const animationStylesMatch = animationStyles.match(regExp);
		if (animationStylesMatch) {
			scale = +animationStylesMatch[1].split(',')[0];
		}
	}

	return getRoundedValue(scale);
};

export const getStylesWithScaledNumbers = (stylesString: string, scale: number): string => {
	const styleNames = [
		'font-size',
		'line-height',
		'min-height',
		'padding-top',
		'letter-spacing',
	];
	const regExp = new RegExp(styleNames
		.map(styleName => `${styleName}:\\s*([+-]?\\d+\\.\\d+|[+-]?\\d+)`)
		.join('|'), 'g');

	return stylesString.replace(regExp, (match, ...groups) => {
		const index = styleNames.findIndex(styleName => match.includes(styleName));
		const size = groups[index];
		const scaledNumber = getRoundedValue(getNumberFromString(size) * scale);

		return `${styleNames[index]}:${scaledNumber}`;
	});
};

export const getScaleFromStyle = ({ transform }: StyleProps): number => {
  const regExp = /scale\((.+?)\)/;
  const match = transform && transform.match(regExp);
  return match ? +match[1] : 1;
};

export const getTransformStyle = ({ transform }: StyleProps): string => {
  const regExp = /translate\((.+?)\)/;
  const match = transform && transform.match(regExp);
  return match ? match[0] : '';
};

export const getTranslateValueFromStyle = ({ transform }: StyleProps): number[] => {
	const regExp = /translate\((.+?)\)/;
	const match = transform && transform.match(regExp);

	if (!match) {
		return [0, 0];
	}

	const value = match[1];
	return value.split(',').map(item => getNumberFromString(item));
};

export const getCorrectedLetterSpacing = (letterSpacing: string, scale: number): number => {
	const letterSpacingValue = getNumberFromString(letterSpacing);
	// We have to ignore small letter-spacing because inDesign sets it almost on each word (span element) and it will cause a huge amount of generated spans in Rich text editors
	// 2px from inDesign === 2px / 20 = 0.1px in backoffice
	const IGNORED_LETTER_SPACING_VALUE = 2;
	const isIgnoredLetterSpacing = Math.abs(letterSpacingValue) < IGNORED_LETTER_SPACING_VALUE;

	return isIgnoredLetterSpacing
		? 0
		: getRoundedValue(letterSpacingValue * scale, 10);
};

export const getTooltipAction = (action: TooltipAction, tooltip: string = ''): string => (
	// tslint:disable-next-line: max-line-length
	`javascript:document.dispatchEvent(new CustomEvent("WebPublicationAction", {"detail":{"type":"${action}","params":{"content": "${tooltip}"},"event":typeof(event) !== "undefined" ? event : undefined}})); return false;`
);

// get tooltip in case it was set in inDesign (for video)
export const getDefaultTooltip = (value: string): string => {
	if (!value) {
		return '';
	}

	const regExp = /"params":{"content": "(.+?)"}/;
	const match = value.match(regExp);
	return match ? match[1] : '';
};

export const setElementIsEdited = (element: HTMLElement): void => {
	if (!element.classList.contains(editedElementClassName)) {
		element.classList.add(editedElementClassName);
	}
};

export const getUrlWithoutOrigin = (url: string, isHtmlEditor?: boolean): string => {
	let modifiedUrl = url;

	if (isHtmlEditor) {
		if (UserAgentUtils.isMobileApp) {
			const FILES_PATH = 'www/v2/';
			if (url.indexOf(FILES_PATH) !== -1) {
				modifiedUrl = url.substring(url.indexOf(FILES_PATH) + FILES_PATH.length);
			}
		} else {
			const { href, search, hash } = location;
			const origin = href.replace(`${search}${hash}`, '');
			modifiedUrl = url.replace(origin, '');
		}
	}

	const regExp = /\$media\$|\${media}|\$%7Bmedia%7D|publication\/contents/;
	const match = modifiedUrl.match(regExp);

	return match && match.index
		// we have to replace some encoded characters to its actual values for proper handling of urls in Reader
		? modifiedUrl.substring(match.index).replace(/%7B/g, '{').replace(/%7D/g, '}')
		: modifiedUrl;
};

export const getResourceUrl = (url: string, config: Api.Config, resourceId: number, isHtmlEditor?: boolean): string => {
	const urlWithoutOrigin = getUrlWithoutOrigin(url, isHtmlEditor);

	if (isYoutubeLink(urlWithoutOrigin)) {
		return urlWithoutOrigin;
	} else if (hasMediaParam(urlWithoutOrigin)) {
		return UrlUtils.getEditorResourceUrl(config, undefined, getRelUrlFromStringWithMediaParam(urlWithoutOrigin));
	}

	return UrlUtils.getEditorResourceUrl(config, resourceId, urlWithoutOrigin);
};

export const getBorderWidth = (scale: number, minWidth: number = 2): number => (
	getRoundedValue(minWidth / scale)
);

export const getLinkType = (href: string): LinkType => {
	if (!href) {
		return LinkType.url;
	}

	return href.includes('http') ? LinkType.url : LinkType.gotoPage;
};

export const getGotoPageAction = (page: string): string => (
	`javascript:document.dispatchEvent(new CustomEvent('WebPublicationAction', {'detail':{'type':'goToPage','params':{'page':${page}}}}));return false;`
);

export const getPageFromGotoPageAction = (value: string): string => {
	if (!value) {
		return '';
	}

	const regExp = /'params':{'page':(.+?)}/;
	const match = value.match(regExp);
	return match ? match[1] : '';
};

export interface PositionAndSize {
	width: number;
	height: number;
	x: number;
	y: number;
}

export const getImageCenteredPositionAndSize = (
	frameWidth: number,
	frameHeight: number,
	imageWidth: number,
	imageHeight: number,
	isPercentageValues?: boolean
): PositionAndSize => {
	let width = imageWidth;
	let height = imageHeight;
	let scale = 1;

	const isImageWiderThanFrame = imageWidth > frameWidth;

	if (isImageWiderThanFrame) {
		width = frameWidth;
		scale = frameWidth / imageWidth;
		height = imageHeight * scale;
	}

	const isImageHigherThanFrame = height > frameHeight;

	if (isImageHigherThanFrame) {
		height = frameHeight;
		scale = frameHeight / imageHeight;
		width = imageWidth * scale;
	}

	const centeredX = (frameWidth - width) / 2;
	const centeredY = (frameHeight - height) / 2;

	return {
		width: isPercentageValues ? (width / frameWidth * 100) : width,
		height: isPercentageValues ? (height / frameHeight * 100) : height,
		x: isPercentageValues ? (centeredX / frameWidth * 100) : centeredX,
		y: isPercentageValues ? (centeredY / frameHeight * 100) : centeredY,
	};
};

export const getOptimizedImage = (driveItem: Api.DriveItem, width: number, height: number, maxWidth: number): OptimizedImageData => {
	const { driveList: { resolutions } } = store.getState();
	const { associatedResolutions, id, src, name } = driveItem;
	const optimizedImage = {
		width,
		height,
		src,
	};

	if (associatedResolutions) {
		const availableResolutionsId = associatedResolutions[id] || 'default';
		const optimizedSize = [...resolutions[availableResolutionsId].sizes]
			.sort((a, b) => a.width - b.width)
			.find(({ width }) => width >= maxWidth);

		if (optimizedSize) {
			const fileExtension = Utils.getFileExtension(name);
			optimizedImage.height = height * (optimizedSize.width / width);
			optimizedImage.width = optimizedSize.width;
			optimizedImage.src = src.replace(`.${fileExtension}`, `_${optimizedSize.box ? 'box-' : ''}${optimizedSize.width}x${optimizedSize.height}.${fileExtension}`);
		}
	}

	return optimizedImage;
};

export const getStringWithoutSpaces = (str: string): string => (
	str.trim().replace(/>\s+</g, '><')
);

export const isStyleIncludesHeight = (style: string): boolean => {
	const styleWithoutSpaces = style.replace(/\s+/g, '');
	return styleWithoutSpaces.includes('height') && (
		styleWithoutSpaces.indexOf('height') === 0 ||
		styleWithoutSpaces[styleWithoutSpaces.indexOf('height') - 1] === ';'
	);
};

export const isEditableButton = (linkElement: HTMLAnchorElement): boolean => (
	[...linkElement.children].some(child => (
		child.className.includes(constants.editableButtonGraphicsClassName)
	))
);

export const getStylesArrayWithoutSpaces = (styles: string): string[] => (
	styles.split(';').reduce((acc: string[], current: string) => {
		if (current) {
			const [key, value] = current.split(':');
			acc.push(`${key.trim()}:${value.trim()}`);
		}
		return acc;
	}, [])
);

const MAX_FONT_SIZE = 2000; // Firefox doesn't render a font-size bigger than 2000px;
export const hasMaxFontSize = (content: string, scale: number): boolean => {
	const regExp = /font-size:\s*(\d+\.\d+|[+-]?\d+)/g;
	const match = content.match(regExp);

	if (!match) {
		return false;
	}

	return match.some(item => (getNumberFromString(item) / scale) > MAX_FONT_SIZE);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/helpers.ts